import messaging, { FirebaseMessagingTypes, getToken } from '@react-native-firebase/messaging';
import notifee, { AndroidImportance, AndroidStyle } from '@notifee/react-native';
import { Platform, PermissionsAndroid } from 'react-native';
import i18n from '@shared/i18n';
import { getChatPushTitle } from '@shared/utils/chatPushTitle';
import { Telemetry } from './telemetry/Telemetry';

const CHANNELS = {
  CHAT: { id: 'sendbird_messages', name: 'Chat Messages' },
  REMEASUREMENT: { id: 'remeasurement_alerts', name: 'Remeasurement Alerts' },
  REPORT: { id: 'report_notifications', name: 'Report Notifications' },
  DEFAULT: { id: 'default_channel', name: 'General Notifications' },
} as const;

let isInitialized = false;

export const initializeNotifications = async (): Promise<void> => {
  if (isInitialized) return;

  await requestPermissions();
  if (Platform.OS === 'android') {
    await createNotificationChannels();
  }
  setupMessageHandlers();

  isInitialized = true;
};

const createNotificationChannels = async (): Promise<void> => {
  await Promise.all(Object.values(CHANNELS).map((c) => notifee.deleteChannel(c.id)));

  const channels = Object.values(CHANNELS).map((channel) => ({
    ...channel,
    importance: AndroidImportance.HIGH,
    sound: 'ringtone_medicilio',
  }));

  await Promise.all(channels.map((c) => notifee.createChannel(c)));
};

export const requestPermissions = async (): Promise<boolean> => {
  if (Platform.OS === 'android') {
    if (Platform.Version >= 33) {
      const granted = await PermissionsAndroid.request(PermissionsAndroid.PERMISSIONS.POST_NOTIFICATIONS);
      return granted === PermissionsAndroid.RESULTS.GRANTED;
    }
    return true;
  }

  if (Platform.OS === 'ios') {
    const authStatus = await messaging().requestPermission();
    return (
      authStatus === messaging.AuthorizationStatus.AUTHORIZED ||
      authStatus === messaging.AuthorizationStatus.PROVISIONAL
    );
  }

  return false;
};

export const getFCMToken = async (): Promise<string | null> => {
  try {
    return await getToken(messaging());
  } catch {
    return null;
  }
};

export const onFCMTokenRefresh = (listener: (token: string) => void): (() => void) =>
  messaging().onTokenRefresh(listener);

const setupMessageHandlers = (): void => {
  messaging().setBackgroundMessageHandler(handleNotificationData);
  messaging().onMessage(handleNotificationData);
};

const parseSendbirdData = (data: any): any => {
  if (!data?.sendbird) return null;
  if (typeof data.sendbird === 'string') {
    try {
      return JSON.parse(data.sendbird);
    } catch {
      return null;
    }
  }
  return data.sendbird;
};

const handleNotificationData = async (remoteMessage: FirebaseMessagingTypes.RemoteMessage): Promise<void> => {
  const { data, notification } = remoteMessage;
  const sendbirdData = parseSendbirdData(data);

  const title = sendbirdData?.push_title || notification?.title || i18n.t('notifications.default.title');
  const body = sendbirdData?.message || notification?.body || i18n.t('notifications.default.body');

  if (sendbirdData?.notification_push_data?.page) {
    const params = {
      ...sendbirdData.notification_push_data,
      ...sendbirdData.notification_user_variables,
    };

    await displayNotification({
      title,
      body,
      data: {
        type: 'page_navigation',
        page: sendbirdData.notification_push_data.page,
        params: JSON.stringify(params),
      },
      channelId: CHANNELS.DEFAULT.id,
    });
    return;
  }

  const channelUrl = sendbirdData?.channel?.channel_url || (data?.channel_url as string);
  if (channelUrl) {
    await displayNotification({
      title: getChatPushTitle(
        sendbirdData,
        {
          technicalSupport: i18n.t('screens.chat.channelTitle.technicalSupport'),
          team: i18n.t('screens.chat.channelTitle.team'),
        },
        title,
      ),
      body,
      data: {
        type: 'chat',
        channelUrl,
      },
      channelId: CHANNELS.CHAT.id,
    });
    return;
  }

  await displayNotification({
    title,
    body,
    data: { type: 'generic', ...data },
    channelId: CHANNELS.DEFAULT.id,
  });
};

interface DisplayNotificationParams {
  title: string;
  body: string;
  data: Record<string, string>;
  channelId: string;
}

const displayNotification = async ({ title, body, data, channelId }: DisplayNotificationParams): Promise<void> => {
  try {
    await notifee.displayNotification({
      title,
      body,
      data,
      android: {
        channelId,
        importance: AndroidImportance.HIGH,
        smallIcon: 'ic_notification',
        style: { type: AndroidStyle.BIGTEXT, text: body },
        pressAction: { id: 'default' },
      },
      ios: {
        sound: 'ringtone_medicilio.mp3',
      },
    });
  } catch (error) {
    Telemetry.error(error, { event:'push.display.fail', domain:'push' });
  }
};

